<template>
    <div class="head">
        <div class="title">{{ trans.cart.title }}</div>
        <GoBackButton @click.prevent="onGoBackButtonClick" />
        <UserMenuComponent />
        <!-- Mobile POS only: the operator version of the app -->
        <DiscountButton
            v-if="areDiscountsEnabled"
            style="position: absolute; right: 72px; top: 8px; pointer-events: all"
            @onClick="onDiscountButtonClick"
        />
        <LanguageSelector v-if="kiosk.isKioskMode" />
    </div>

    <div ref="itemListElement" class="itemlist scrollable">
        <div v-if="isEmpty" class="nodata">
            <div class="lbl">{{ trans.cart.noItems }}</div>
            <a class="backlink" @click="onGoBackButtonClick">{{ trans.cart.backToOverview }}</a>
        </div>
        <CartItem
            v-for="item in cartItems"
            :key="item.id"
            :item="item"
            :disabled="isPerformingAction"
            @decrease="onDecreaseItemSelected"
            @increase="onIncreaseItemSelected"
            @remove="onRemoveItemSelected"
        />
    </div>

    <div class="bottom">
        <!-- Mobile POS only: the cart rows are only selectable while discounts are on -->
        <SelectAllButton :isShown="displaySelectAllButton" @click="onSelectAllClick" />
        <ClearSelectionButton :isShown="displayClearSelectionButton" @click="onClearSelectionClick" />
        <div class="selectionsum">{{ selectedArticlesText }}</div>
        <PayButtonComponent
            v-if="isOrderMode"
            :action="trans.general.confirm"
            :disabled="!isPayButtonEnabled"
            :amount="totalAmount"
            @click="onProceedButtonClick"
        />
        <PayButtonComponent v-if="!isOrderMode" :action="trans.pay.orderWithStaff" :disabled="true" />
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { CartController } from "@/app/controller/CartController";
    import { trans } from "@/app/localization/Trans";
    import { CartApplicableDiscountsListPopover } from "@/app/popover/CartApplicableDiscountsListPopover";
    import { MinimumAgePopover } from "@/app/popover/MinimumAgePopover";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { Helper } from "@/app/ui/Helper";
    import { Popup } from "@/app/ui/Popup";
    import type { ICartItem } from "@/data/model/Customer";
    import { CartControllerMode, ServiceType } from "@/data/model/Enums";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

    import CartItem from "@/app/components/cart/CartItem.vue";
    import PayButtonComponent from "@/app/components/footer/PayButtonComponent.vue";
    import GoBackButton from "@/app/components/header/GoBackButton.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";
    import LanguageSelector from "@/app/components/language/LanguageSelector.vue";
    import ClearSelectionButton from "@/components/elements/ClearSelectionButton.vue";
    import SelectAllButton from "@/components/elements/SelectAllButton.vue";
    import DiscountButton from "@/operator/components/offer/DiscountButton.vue";

    // import CartItem2 from "@/components/cart/CartItem.vue";
    const cart = useCartStore();
    const kiosk = useKioskStore();
    const identity = useIdentityStore();
    const offer = useOfferStore();

    // header
    const isViewVisible = ref(false);

    CartController.onShown.add(onBecomeVisible);
    CartController.onHidden.add(onBecomeInvisible);
    CartController.onUpdateMode.add(updateMode);

    // init components
    onMounted(() => {
        const service = Helper.getCurrentOrderOrOfferService();
        const cartMode =
            service!.serviceType === ServiceType.Offer ? CartControllerMode.Offer : CartControllerMode.Order;

        constructor(cartMode);
    });

    onBeforeUnmount(() => {
        TastierClient.context.onCartChanged.remove(updateCart);
    });

    const selectedArticles = ref(0);
    const selectedArticlesText = computed(() => trans.cart.itemsSelected.replace("{0}", selectedArticles.value + ""));

    const areDiscountsEnabled = computed(() => offer.areDiscountsEnabled && identity.hasOperatorAccess);

    // the same selection the bill screen offers for its items, on the cart rows this screen makes
    // selectable while discounts are on
    const isCartSelectionActive = computed(() => cart.hasItems && areDiscountsEnabled.value);
    const displaySelectAllButton = computed(() => isCartSelectionActive.value && !cart.isAnyCartItemSelected);
    const displayClearSelectionButton = computed(() => isCartSelectionActive.value && cart.isAnyCartItemSelected);

    function onSelectAllClick() {
        cart.selectAllCartItems();
    }

    function onClearSelectionClick() {
        cart.deselectAllCartItems();
    }

    function onDiscountButtonClick() {
        if (!areDiscountsEnabled.value || cart.currentCart == null) return;

        // the app carries no bootstrap modal, so this uses the same popover the app applies
        // bill discounts with
        new CartApplicableDiscountsListPopover({
            locationId: cart.locationId!,
            cartId: cart.currentCart.cartId,
            selectedMainCartItemIds: cart.selectedMainCartItemIds,
        }).show();
    }

    const currentMode = ref<CartControllerMode>(CartControllerMode.Order);
    const isOrderMode = computed(() => currentMode.value === CartControllerMode.Order);

    const totalAmount = ref<string>(toMoneyWithSign(0, true));
    const isPayButtonEnabled = ref(false);

    const itemListElement = ref<HTMLDivElement | null>(null);
    const $itemList = computed<JQuery | null>(() => (itemListElement.value ? $(itemListElement.value) : null));

    const cartItems = ref<Array<ICartItem>>([]);

    function constructor(mode: CartControllerMode) {
        currentMode.value = mode;
        isPayButtonEnabled.value = false;

        onBecomeVisible();
    }

    async function onBecomeVisible() {
        isViewVisible.value = true;

        TastierClient.context.onCartChanged.remove(updateCart);
        TastierClient.context.onCartChanged.add(updateCart);
        TastierClient.context.onCartDiscountChanged.remove(onCartDiscountChanged);
        TastierClient.context.onCartDiscountChanged.add(onCartDiscountChanged);
        $itemList.value?.scrollTop(0);

        updateCart(true);
    }

    function onBecomeInvisible() {
        isViewVisible.value = false;
        TastierClient.context.onCartChanged.remove(updateCart);
        TastierClient.context.onCartDiscountChanged.remove(onCartDiscountChanged);
    }

    function updateMode(mode: CartControllerMode) {
        currentMode.value = mode;
    }

    function onGoBackButtonClick() {
        CartController.onGoBack.dispatch();
    }

    function onProceedButtonClick() {
        if (!cart.isMinimumTotalUnitsPerOrderReached()) {
            Popup.showWithOptions({
                message: translations.cart.minimumTotalUnitsPerOrderNotReached.replace(
                    "{0}",
                    cart.getMissingUnitsToReachMinimumTotalUnitsPerOrder() + ""
                ),
                confirmButton: translations.general.ok,
                uniqueId: "minimumTotalUnitsPerOrderNotReached",
            });
            return;
        }

        checkMinimumAgeAndContinue();
    }

    const isEmpty = ref(false);

    async function onCartDiscountChanged() {
        updateCart(true);
    }

    async function updateCart(isUserAction: boolean) {
        const offer = TastierClient.context.currentOffer;
        let articles = 0;

        if (currentMode.value === CartControllerMode.Order && offer !== undefined && offer.cart !== undefined) {
            totalAmount.value = toMoneyWithSign(offer.cart.totalPayableAmount);
            isPayButtonEnabled.value =
                offer.cart.items.length > 0 &&
                (offer.cart.totalPayableAmount >= 0 || offer.cart.totalPayableAmount < 0);
        } else {
            isPayButtonEnabled.value = false;
        }

        if (offer !== undefined && offer.cart !== undefined) {
            for (const item of offer.cart.items) {
                articles += item.quantity;
            }

            cart.setCurrentCart(offer.cart);
        }

        selectedArticles.value = articles;

        if (!isUserAction) {
            Popup.showWithOptions({
                message: `<b>${translations.pay.cartChangedTitle}</b><br /><br />${translations.pay.cartChangedMessage}`,
                uniqueId: "totalChanged",
            });
        }

        if ($itemList.value === null) {
            logError("itemListElement is not set");
        }

        // get last scroll position
        const lastScrollPos = $itemList.value?.scrollTop() ?? 0;

        if (offer === undefined || offer.cart === undefined || offer.cart.items.length === 0) {
            cartItems.value = [];
            isEmpty.value = true;
            return;
        }

        isEmpty.value = false;

        cartItems.value = offer!.cart!.items;
        await nextTick();

        // scroll to last position
        $itemList.value?.scrollTop(lastScrollPos);
    }

    const isPerformingAction = ref(false);

    function checkMinimumAgeAndContinue() {
        const offer = TastierClient.context.currentOffer;
        if (offer === undefined || offer.cart === undefined) return;

        let minAge = 0;

        const searchMinAgeRecusrive = (itm: ICartItem) => {
            if (itm.minAge !== undefined && itm.minAge > minAge) minAge = itm.minAge;

            if (itm.options !== undefined && itm.options.length > 0) {
                for (const optionGroup of itm.options) {
                    for (const optionItem of optionGroup.items) {
                        searchMinAgeRecusrive(optionItem);
                    }
                }
            }
        };

        for (const item of offer.cart.items) {
            searchMinAgeRecusrive(item);
        }

        if (minAge === 0) {
            CartController.onContinue.dispatch();
            return;
        }

        new MinimumAgePopover(minAge, () => {
            CartController.onContinue.dispatch();
        }).show();
    }

    async function onDecreaseItemSelected(cartItem: ICartItem) {
        isPerformingAction.value = true;
        try {
            await TastierClient.context.changeCartItemQuantity(cartItem.id, cartItem.quantity - 1);
        } catch (ex) {
            logError(ex);
        } finally {
            isPerformingAction.value = false;
        }
    }

    async function onIncreaseItemSelected(cartItem: ICartItem) {
        isPerformingAction.value = true;
        try {
            await TastierClient.context.changeCartItemQuantity(cartItem.id, cartItem.quantity + 1);
        } catch (ex) {
            logError(ex);
        } finally {
            isPerformingAction.value = false;
        }
    }

    async function onRemoveItemSelected(cartItem: ICartItem) {
        isPerformingAction.value = true;
        try {
            await TastierClient.context.removeItemFormCart(cartItem.id);
        } catch (ex) {
            logError(ex);
        } finally {
            isPerformingAction.value = false;
        }
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .app > .cart {
        > .itemlist {
            position: absolute;
            top: 53px;
            bottom: 104px;
            left: 0;
            right: 0;
            padding: 0 22px;

            > .item {
                position: relative;
                border-bottom: 1px solid #f5f5f5;
                padding: 24px 0;

                // the image takes a column of its own, the head, the option groups and the
                // comment editor share the column next to it and keep their indentation
                &.has-image {
                    display: grid;
                    grid-template-columns: auto minmax(0, 1fr);

                    // spans the head, the option groups and the comment editor
                    > .image {
                        grid-row: 1 / span 3;
                    }

                    > .head,
                    > .subs,
                    > .comment-editor-container,
                    > .cart-item-comment-editor {
                        grid-column: 2;
                    }
                }

                > .image {
                    width: 36px;
                    height: 36px;
                    margin-right: 12px;

                    > img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                    }
                }

                &.has-discount {
                    padding-top: 1.725em;

                    .head {
                        margin-bottom: -0.625em;
                    }
                }

                .item-price.has-discount {
                    cursor: pointer;
                    transition: opacity 0.2s variables.$easeOutExpo;

                    &:hover {
                        opacity: 0.5;
                    }
                }

                > .head {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    justify-content: space-between;
                    line-height: 18px;

                    // only rendered while the row is selectable (Mobile POS with discounts on)
                    > .quantity-selection-checkbox {
                        flex: 0 0 auto;
                        margin-right: 8px;
                    }

                    > .quantity {
                        color: var(--colorPrimary);
                        font-size: 16px;
                        font-weight: 600;
                        flex: 0 0 28px;
                    }

                    // the only column that may shrink: without min-width a long word keeps the name
                    // at its full width and pushes the price and the remover off a small screen
                    > .name {
                        font-size: 16px;
                        text-align: left;
                        white-space: normal;
                        flex-grow: 1;
                        min-width: 0;
                        word-break: break-word;
                    }

                    > .increaser {
                        flex: 0 0 52px;
                        font-size: 0;
                        white-space: nowrap;

                        > .btn {
                            display: inline-block;
                            vertical-align: top;
                            background-color: transparent;
                            border: none;
                            padding: 4px;
                            margin: -4px 0;

                            > .outer {
                                height: 18px;
                                width: 18px;
                                border-radius: 50%;
                                text-align: center;

                                > .fas {
                                    line-height: 18px;
                                    font-size: 18px;
                                }
                            }

                            &.decrease {
                                > .outer > .fas {
                                    color: colors.$orange;
                                }

                                &:disabled > .outer > .fas {
                                    color: colors.$orangeHover;
                                }
                            }

                            &.increase {
                                > .outer > .fas {
                                    color: var(--colorPrimary);
                                }

                                &:disabled > .outer > .fas {
                                    color: var(--colorPrimaryHover);
                                }
                            }

                            &:disabled {
                                cursor: pointer !important;
                            }

                            &.disabled {
                                cursor: pointer !important;

                                > .outer > .fas {
                                    color: #cccccc !important;
                                }
                            }
                        }
                    }

                    > .price {
                        flex: 0 0 60px;
                        text-align: right;

                        &.has-discount {
                            position: relative;
                            top: -0.625em;
                        }
                    }

                    > .remover {
                        flex: 0 0 20px;
                        margin: -10px;
                        padding: 10px;
                        text-align: right;
                        cursor: pointer;
                        font-size: 0;

                        > .outer {
                            display: inline-block;
                            vertical-align: top;
                            height: 12px;

                            > .fal {
                                line-height: 16px;
                                font-size: 12px;
                                color: colors.$lightFontColor;
                            }
                        }
                    }
                }

                .subs {
                    padding: 2px 0 2px 22px;

                    > .group {
                        padding-bottom: 4px;

                        > .name {
                            font-style: italic;
                            font-size: 12px;
                            line-height: 18px;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }

                        > .entries {
                            > .item {
                                font-size: 12px;
                                line-height: 20px;

                                > .head {
                                    display: flex;
                                    flex-direction: row;
                                    flex-wrap: nowrap;
                                    justify-content: space-between;

                                    > .name {
                                        > .quantity {
                                            display: inline-block;
                                            vertical-align: top;
                                            padding-right: 3px;
                                            font-weight: 600;
                                        }

                                        flex-grow: 1;
                                    }

                                    > .price {
                                        flex: 0 0 56px;
                                        text-align: right;
                                        padding-right: 20px;
                                    }
                                }
                            }
                        }
                    }
                }

                > .subs {
                    padding-top: 6px;
                    padding-left: 28px;
                    margin-bottom: -12px;
                }

                > .comment-editor-container,
                > .cart-item-comment-editor {
                    padding-left: 28px;
                    margin-bottom: -12px;
                }
            }

            > .nodata {
                padding: 40px 20px 0 20px;

                > .lbl {
                    text-align: center;
                    font-size: 14px;
                    padding: 12px 0;

                    .kiosk-mode & {
                        padding: 12px * variables.$kioskScale 0;
                        font-size: 16px * variables.$kioskScale;
                    }
                }

                > .backlink {
                    color: var(--colorPrimary);
                    text-decoration: none;
                    display: block;
                    text-align: center;
                    padding: 4px;
                    font-size: 14px;
                    cursor: pointer;

                    @include mixins.hover-and-active() {
                        color: var(--colorPrimaryHover);
                    }

                    .kiosk-mode & {
                        font-size: 16px * variables.$kioskScale;
                        padding: 4px * variables.$kioskScale;
                    }
                }
            }
        }

        > .bottom {
            height: 104px;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 220;
            padding: 0 12px;
            background-color: #fff;
            -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);

            // both buttons hover above the bar, over the item list, and only one of them is ever
            // shown: while a button is hidden it must not take the taps meant for the row behind it
            > .select-all-button-container:not(.shown),
            > .clear-selection-button-container:not(.shown) {
                pointer-events: none;
            }

            > .selectionsum {
                text-align: center;
                font-size: 14px;
                color: colors.$fontColor;
                height: 38px;
                line-height: 38px;
                letter-spacing: 0.3px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }

    // kiosk: the row is sized in px from top to bottom, so its padding, the image column,
    // the head with its buttons and the indentation of the option groups and the comment
    // editor all have to scale together. the option group and option rows carry their own
    // kiosk overrides in their components
    .kiosk-mode .app > .cart > .itemlist > .item {
        padding: (24px * variables.$kioskScale) 0;

        &.has-discount {
            padding-top: 1.725em * variables.$kioskScale;

            .head {
                margin-bottom: -0.625em * variables.$kioskScale;
            }
        }

        // the kiosk keeps its own image size: the app image was halved for small phones, which
        // the kiosk screen does not have, so these are no longer the app base times the scale
        > .image {
            width: 72px * variables.$kioskScale;
            height: 72px * variables.$kioskScale;
            margin-right: 1rem * variables.$kioskScale;
        }

        // the text of the row follows the buttons: a larger base than the app row, and the
        // columns around the quantity and the price grow with the digits they hold
        > .head {
            line-height: 22px * variables.$kioskScale;
            // the name is the tallest column as soon as it wraps, the rest of the row
            // lines up on its middle instead of hanging from the first line
            align-items: center;

            > .quantity {
                flex: 0 0 (36px * variables.$kioskScale);
                font-size: 20px * variables.$kioskScale;
            }

            > .name {
                font-size: 20px * variables.$kioskScale;
            }

            // the two circles are touch targets, so they are set from a larger base than
            // their app counterparts: 30px circles in 8px of padding, which makes a 69px
            // target and the 92px column that holds the pair
            > .increaser {
                flex: 0 0 (92px * variables.$kioskScale);

                > .btn {
                    padding: 8px * variables.$kioskScale;
                    margin: (-8px * variables.$kioskScale) 0;

                    > .outer {
                        height: 30px * variables.$kioskScale;
                        width: 30px * variables.$kioskScale;

                        > .fas {
                            line-height: 30px * variables.$kioskScale;
                            font-size: 30px * variables.$kioskScale;
                        }
                    }
                }
            }

            // the price has no size of its own, it inherits the one of the head and lays
            // the discounted amount out in em, so this scales both lines
            > .price {
                flex: 0 0 (76px * variables.$kioskScale);
                font-size: 20px * variables.$kioskScale;

                // the app row lifts the two line discounted price to keep it on the first
                // line, here it is centred like every other column
                &.has-discount {
                    top: 0;
                }
            }

            // a touch target as well, from the same kind of larger base as the circles
            > .remover {
                flex: 0 0 (30px * variables.$kioskScale);
                margin: 0;
                padding: 14px * variables.$kioskScale;

                // the line height matches the box, otherwise the glyph hangs below its
                // middle the way it does in the app row
                > .outer {
                    height: 22px * variables.$kioskScale;

                    > .fal {
                        line-height: 22px * variables.$kioskScale;
                        font-size: 22px * variables.$kioskScale;
                    }
                }
            }
        }

        // the nested option groups, at every level
        .subs {
            padding: (2px * variables.$kioskScale) 0 (2px * variables.$kioskScale) (22px * variables.$kioskScale);

            > .group {
                padding-bottom: 4px * variables.$kioskScale;
            }
        }

        > .subs {
            padding-top: 6px * variables.$kioskScale;
            padding-left: 36px * variables.$kioskScale;
            margin-bottom: -12px * variables.$kioskScale;
        }

        // the editor sizes itself in em off its own font size, so the size is all it needs
        > .comment-editor-container,
        > .cart-item-comment-editor {
            padding-left: 36px * variables.$kioskScale;
            margin-bottom: -12px * variables.$kioskScale;
            font-size: 14px * variables.$kioskScale;
        }
    }
</style>
